import Highcharts from "highcharts";
import HighchartsExport from "highcharts/modules/exporting";
import HighchartsReact from "highcharts-react-official";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDebouncedResizeObserver } from "../../../hooks/useDebouncedResizeObserver";
import { DashboardEntity } from "../../../entities/company/Dashboard/DashboardEntity";
import { DashboardFilterEntity } from "../../../entities/company/Dashboard/DashboardFilterEntity";
import { FilterRequest } from "../../../entities/Filter/FilterRequest";
import CompanyService from "../../../services/CompanyService";
import DashboardService from "../../../services/DashboardService";
import { FilterCollection } from "../../../services/FilterService";
import {
  formatCurrency,
  formatInteger,
  formatIntizaDate
} from "../../../utils/FormatUtils";
import { TranslationService } from "../../../services/TranslationService";
import { Cast, OptionalMap } from "../../../utils/Utils";
import { parseDashboardFilter } from "../../../utils/DashboardFilterUtils";
import moment from "moment";
import { Operator } from "../../segment/providers/CriteriaContext";
HighchartsExport(Highcharts);

Highcharts.AST.allowedAttributes.push("onclick");
Highcharts.AST.allowedAttributes.push("onmouseup");
Highcharts.setOptions({
  lang: {
    thousandsSep: window.location.href
      .replace(window.location.origin, "")
      .startsWith("/en/")
      ? ","
      : ".",
    decimalPoint: window.location.href
      .replace(window.location.origin, "")
      .startsWith("/en/")
      ? "."
      : ","
  }
});

type HighchartChartType = {
  dashboard: DashboardEntity;
  options: Highcharts.Options;
  dashboardFilters: FilterRequest[];
  filterCallback: (dashboardFilterEntity: DashboardFilterEntity) => void;
  valuesEnabled: boolean;
};

const HighchartChart = ({
  dashboard,
  options,
  dashboardFilters,
  filterCallback,
  valuesEnabled
}: HighchartChartType) => {
  const [hoveredPoint, setHoveredPoint] = useState<Highcharts.Point | null>(
    null
  );
  const [contextHovered, setContextHovered] =
    useState<Highcharts.TooltipFormatterContextObject>();
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  useDebouncedResizeObserver(chartContainerRef, () => {
    chartRef.current?.chart?.reflow();
  });

  const chartOptionHovered = (pointEvent: Highcharts.Point) => {
    setHoveredPoint(pointEvent);
  };

  const chartOptionClicked = (
    pointEvent: Highcharts.SeriesClickEventObject
  ) => {
    const pointIndex = hoveredPoint?.index ?? pointEvent.point.index;
    const data: Array<any> = (
      (highchartOptions.series![0] ?? highchartOptions.series) as any
    ).data[pointEvent.point.index];
    // const value = (dashboard?.categoryValues ? dashboard.categoryValues![pointEvent.point.index] : OptionalMap((data[3] ?? undefined) as string, x => [...x].filter(x => x === "-").length === 2 ? x.replaceAll("-", "/") : x) || "");
    const value = dashboard?.categoryValues
      ? dashboard.categoryValues![pointIndex]
      : OptionalMap((data[3] ?? undefined) as string, (x) =>
          [...x].filter((x) => x === "-").length === 2
            ? x.replaceAll("-", "/")
            : x
        ) || "";
    const filterEntity = dashboard?.categoryValues
      ? dashboard.GroupBy
      : data[2];

    const filter: DashboardFilterEntity = {
      dashboard,
      filterEntity,
      value,
      option: data[0] || pointEvent.point.category,
      operator: data[8] ?? "0"
    };
    filterCallback(filter);
  };

  if (options?.tooltip) {
    options.tooltip!.useHTML = true;
    options.tooltip!.formatter = function () {
      return formatTooltip(this);
    };
    options.plotOptions!.series!.events = {
      click: chartOptionClicked,
      mouseOver: function (this: Highcharts.Series, event: any) {
        chartOptionHovered(event.target); // `target` es un `Highcharts.Point`
      }
    };
  }

  if (options.series && "data" in options.series && options.colors) {
    // @ts-ignore
    const colors = (options.series as { data: string[][] }).data.map(
      (x) => x[4]
    );
    for (let index = 0; index < colors.length; index++) {
      const color = colors[index];
      if (color) {
        options.colors[index] = color;
      }
    }
  }

  const [highchartOptions, setHighchartOptions] =
    useState<Highcharts.Options>(options);
  const { currentLanguage } = TranslationService;
  const history = useHistory();

  useEffect(() => {
    Highcharts.setOptions({
      lang: {
        decimalPoint: TranslationService.currentLanguage === "en" ? "," : "."
      }
    });
  }, []);

  /*ITS OK TO SET 2 TIMES*/
  useEffect(() => {
    setHighchartOptions(options);
    setHighchartOptions({ ...options });
  }, [options]);

  const formatTooltip = (context: Highcharts.TooltipFormatterContextObject) => {
    setContextHovered({ ...context });
    const percentage =
      ((context.y ?? 0) /
        (context.series.data
          .map((x) => x.y)
          .reduce((x, y) => (x ?? 0) + (y ?? 0)) ?? 1)) *
      100;
    const realPercentage =
      percentage === 100 && context.percentage < 100
        ? context.percentage
        : percentage;
    let tooltip = `<div class="containterTooltipTitle"><b>${dashboard.Variable > 1 ? formatCurrency(context.y ?? 0, CompanyService.getDefaultCurrencyId()) : context.y}</b> (${formatInteger(realPercentage)}%)</div>`;

    if (dashboard.Type !== 9) {
      tooltip += `<div class="containerButtonTooltip"><button type="button" class="btn-link-dashboard" onmouseup="goToClient${dashboard.DashboardID.toString().replace("-", "")}(event, ${context.point.index});">${TranslationService.translate.ViewCustomers}</a>`;
    }
    tooltip += `<button type="button" class="btn-link-dashboard brtp" onmouseup="goToInvoice${dashboard.DashboardID.toString().replace("-", "")}(event, ${context.point.index});">${TranslationService.translate.ViewInvoices}</a></div>`;
    return tooltip;
  };

  Cast<Record<string, (event: MouseEvent, index: number) => void>>(window)[
    "goToClient" + dashboard.DashboardID.toString().replace("-", "")
  ] = function (event: MouseEvent, index: number) {
    goTo("/client", index, FilterCollection.Client, event.button === 1);
  };

  Cast<Record<string, (event: MouseEvent, index: number) => void>>(window)[
    "goToInvoice" + dashboard.DashboardID.toString().replace("-", "")
  ] = function (event: MouseEvent, index: number) {
    goTo(
      "/report/iolist",
      index,
      FilterCollection.ReportInvoice,
      event.button === 1
    );
  };

  const goTo = function (
    url: string,
    index: number,
    filterType: FilterCollection,
    newTab: boolean
  ) {
    let addExtraFilters = false;

    const totalFilters = [...dashboardFilters];
    const series = (highchartOptions.series![0] ??
      highchartOptions.series) as any;

    const data: Array<any> = series.data[index];
    const idMap: Record<string | number, string> = {
      "-33": "-10", // -33 Vencimiento
      "-29": "-7", // -29 Segmentos
      "-36": "-8" // -36 Aging
    };
    const isDueNoDue =
      Array.isArray(highchartOptions.series) &&
      highchartOptions.series?.some(
        (el) => el?.name && ["Not due", "Due"].includes(el.name)
      );

    // VENCIMIENTO, con solo 2 elementos, vencido y no vencido - ok
    if (data[2] === -33 && series.data.length === 2) {
      if (index === 0) {
        // No Vencido de hoy en adelante la fecha de vencimiento de facturas
        totalFilters.splice(
          totalFilters.findIndex((x) => x.field === "-1012"),
          1
        );
        const value =
          data[3] === "1"
            ? formatIntizaDate(new Date(0)) +
              "-" +
              formatIntizaDate(moment().add(-1, "day").toDate())
            : formatIntizaDate(new Date()) +
              "-" +
              formatIntizaDate(new Date(2200, 0));

        const dateOperator = 0;
        totalFilters.push({
          operator: dateOperator,
          field: "-1024",
          value: value
        });
      } else {
        // Fecha de vencimiento de facturas menor a hoy
        totalFilters.splice(
          totalFilters.findIndex((x) => x.field === "-1012"),
          1
        );
        const value =
          formatIntizaDate(
            new Date(new Date().setFullYear(new Date().getFullYear() - 50))
          ) +
          "-" +
          formatIntizaDate(moment().add(-1, "day").toDate());

        const dateOperator = 0;
        totalFilters.push({
          operator: dateOperator,
          field: "-1024",
          value: value
        });
      }
    }
    // Aging
    else if (data[2] === -36) {
      const val = CompanyService.GetAgeingValues()[data[3]];
      totalFilters.push({
        field: "-8",
        operator: data[8],
        value: val.filterVal
      });
      if (!(filterType === FilterCollection.Client)) {
        addExtraFilters = true;
      }
    }
    // Segmentos
    else if (data[2] === -29) {
      const value =
        OptionalMap((data[3] as string) ?? undefined, (x) =>
          [...x].filter((x) => x === "-").length === 2
            ? x.replaceAll("-", "/")
            : x
        ) ?? "";
      const i = totalFilters.push({
        field: idMap[data[2]] ?? data[2],
        operator: data[8],
        value
      });

      const fixDue = totalFilters.find(
        (x) => x.field === "-10" && x.value === "1"
      );
      if (fixDue) {
        fixDue.operator = 2;
        fixDue.value = "0";
      }
      const fixDue2 = totalFilters.find((x) => x.field === Cast<string>(-8));
      const val = CompanyService.GetAgeingValues()[parseInt(data[3])];
      if (fixDue2 && val) {
        fixDue2.value = val.filterVal;
      }
      if (
        data[8] === "0" &&
        value === "" &&
        data[0] === TranslationService.translate.Empty
      ) {
        totalFilters[i - 1].operator = 7;
      }

      if (filterType === FilterCollection.ReportInvoice) {
        if (!totalFilters.find((el) => el.field === "-1012")) {
          totalFilters.push({
            operator: 5,
            field: "-1012",
            value: "0"
          });
        }

        if (dashboard.Variable === 3) {
          const value =
            formatIntizaDate(
              new Date(new Date().setFullYear(new Date().getFullYear() - 50))
            ) +
            "-" +
            formatIntizaDate(moment().add(-1, "day").toDate());
          totalFilters.push({
            operator: 0,
            field: "-1024",
            value: value
          });
        }
      }
    }
    // Caso isDue isNoDue
    else if (isDueNoDue) {
      if (dashboard.GroupBy === -10) {
        // Vencimiento
        if (index === 0) {
          totalFilters.splice(
            totalFilters.findIndex((x) => x.field === "-1012"),
            1
          );
          const value =
            data[3] === "1"
              ? formatIntizaDate(new Date(0)) +
                "-" +
                formatIntizaDate(moment().add(-1, "day").toDate())
              : formatIntizaDate(moment().add(-1, "day").toDate()) +
                "-" +
                formatIntizaDate(new Date(2200, 0));

          const dateOperator = 0;
          totalFilters.push({
            operator: dateOperator,
            field: "-1024",
            value: value
          });
        } else {
          totalFilters.splice(
            totalFilters.findIndex((x) => x.field === "-1012"),
            1
          );
          const value =
            formatIntizaDate(
              new Date(new Date().setFullYear(new Date().getFullYear() - 50))
            ) +
            "-" +
            formatIntizaDate(moment().add(-1, "day").toDate());

          const dateOperator = 0;
          totalFilters.push({
            operator: dateOperator,
            field: "-1024",
            value: value
          });
        }
      } else if (dashboard.GroupBy === -8) {
        //Aging
        const val = CompanyService.GetAgeingValues()[index];
        totalFilters.push({
          field: "-8",
          operator: data[8],
          value: val.filterVal
        });
        if (!(filterType === FilterCollection.Client)) {
          addExtraFilters = true;
        }
      } else if (dashboard.GroupBy === -7) {
        // Vencimiento
        if (
          contextHovered?.color === "#1647b5" ||
          contextHovered?.color === "#1647B5"
        ) {
          totalFilters.splice(
            totalFilters.findIndex((x) => x.field === "-1012"),
            1
          );
          const value =
            data[3] === "1"
              ? formatIntizaDate(new Date(0)) +
                "-" +
                formatIntizaDate(moment().add(-1, "day").toDate())
              : formatIntizaDate(new Date()) +
                "-" +
                formatIntizaDate(new Date(2200, 0));

          const dateOperator = 0;
          totalFilters.push({
            operator: dateOperator,
            field: "-1024",
            value: value
          });
        } else {
          totalFilters.splice(
            totalFilters.findIndex((x) => x.field === "-1012"),
            1
          );
          const value =
            formatIntizaDate(
              new Date(new Date().setFullYear(new Date().getFullYear() - 50))
            ) +
            "-" +
            formatIntizaDate(moment().add(-1, "day").toDate());

          const dateOperator = 0;
          totalFilters.push({
            operator: dateOperator,
            field: "-1024",
            value: value
          });
        }
        //Segmentos
        // const dataValues = dashboard.Data?.split(',') ?? [];
        totalFilters.push({
          field: dashboard.GroupBy.toString(),
          operator: 0,
          value: dashboard.categoryValues![contextHovered?.point.index!]
        });

        if (filterType === FilterCollection.ReportInvoice) {
          if (!totalFilters.find((el) => el.field === "-1012")) {
            totalFilters.push({
              operator: 5,
              field: "-1012",
              value: "0"
            });
          }
          if (dashboard.Variable === 3) {
            const value =
              formatIntizaDate(
                new Date(new Date().setFullYear(new Date().getFullYear() - 50))
              ) +
              "-" +
              formatIntizaDate(moment().add(-1, "day").toDate());
            totalFilters.push({
              operator: 0,
              field: "-1024",
              value: value
            });
          }
        }
      } else {
        // Vencimiento
        if (
          contextHovered?.color === "#1647b5" ||
          contextHovered?.color === "#1647B5"
        ) {
          totalFilters.splice(
            totalFilters.findIndex((x) => x.field === "-1012"),
            1
          );
          const value =
            data[3] === "1"
              ? formatIntizaDate(new Date(0)) +
                "-" +
                formatIntizaDate(moment().add(-1, "day").toDate())
              : formatIntizaDate(moment().add(-1, "day").toDate()) +
                "-" +
                formatIntizaDate(new Date(2200, 0));

          const dateOperator = 0;
          totalFilters.push({
            operator: dateOperator,
            field: "-1024",
            value: value
          });
        } else {
          totalFilters.splice(
            totalFilters.findIndex((x) => x.field === "-1012"),
            1
          );
          const value =
            formatIntizaDate(
              new Date(new Date().setFullYear(new Date().getFullYear() - 50))
            ) +
            "-" +
            formatIntizaDate(moment().add(-1, "day").toDate());

          const dateOperator = contextHovered
            ? contextHovered.key === TranslationService.translate.Empty
              ? Operator.Empty
              : contextHovered.key === TranslationService.translate.EqualTo
                ? Operator.Equal
                : Operator.NotEqual
            : 0;

          totalFilters.push({
            operator: dateOperator,
            field: "-1024",
            value: value
          });
        }

        const value =
          (dashboard?.categoryValues
            ? dashboard.categoryValues![contextHovered?.point.index!]
            : OptionalMap((data[3] ?? undefined) as string, (x) =>
                [...x].filter((x) => x === "-").length === 2
                  ? x.replaceAll("-", "/")
                  : x
              ) || "") || "0";

        const operator = contextHovered
          ? contextHovered.key === TranslationService.translate.Empty
            ? Operator.Empty
            : contextHovered.key === TranslationService.translate.EqualTo
              ? Operator.Equal
              : Operator.NotEqual
          : 0;

        const i = totalFilters.push({
          field: dashboard.GroupBy.toString(),
          operator,
          value
        });

        const fixDue = totalFilters.find(
          (x) => x.field === "-10" && x.value === "1"
        );
        if (fixDue) {
          fixDue.operator = 2;
          fixDue.value = "0";
        }

        const fixDue2 = totalFilters.find((x) => x.field === Cast<string>(-8));
        const val = CompanyService.GetAgeingValues()[parseInt(data[3])];
        if (fixDue2 && val) {
          fixDue2.value = val.filterVal;
        }

        if (
          data[8] === "0" &&
          value === "" &&
          data[0] === TranslationService.translate.Empty
        ) {
          totalFilters[i - 1].operator = 7;
        }
      }

      if (!totalFilters.find((el) => el.field === "-1012")) {
        totalFilters.push({
          operator: Operator.NotEqual,
          value: "0",
          field: "-1012"
        });
      }
    }

    // Salida por else adicionales
    else {
      const value =
        OptionalMap((data[3] as string) ?? undefined, (x) =>
          [...x].filter((x) => x === "-").length === 2
            ? x.replaceAll("-", "/")
            : x
        ) ?? "";

      const i = totalFilters.push({
        field: idMap[data[2]] ?? data[2],
        operator: data[8],
        value
      });
      const fixDue = totalFilters.find(
        (x) => x.field === "-10" && x.value === "1"
      );
      if (fixDue) {
        fixDue.operator = 2;
        fixDue.value = "0";
      }
      const fixDue2 = totalFilters.find((x) => x.field === Cast<string>(-8));
      const val = CompanyService.GetAgeingValues()[parseInt(data[3])];
      if (fixDue2 && val) {
        fixDue2.value = val.filterVal;
      }
      if (
        data[8] === "0" &&
        value === "" &&
        data[0] === TranslationService.translate.Empty
      ) {
        totalFilters[i - 1].operator = 7;
      }
    }

    if (
      filterType === FilterCollection.ReportInvoice &&
      dashboard.GroupBy !== -29 &&
      dashboard.Type !== 1
    ) {
      addExtraFilters = true;
    }

    if (
      filterType === FilterCollection.ReportInvoice &&
      dashboard.GroupBy === -29
    ) {
      if (!totalFilters.find((el) => el.field === "-1012")) {
        totalFilters.push({
          operator: 5,
          field: "-1012",
          value: "0"
        });
      }

      if (dashboard.Variable === 3) {
        const value =
          formatIntizaDate(
            new Date(new Date().setFullYear(new Date().getFullYear() - 50))
          ) +
          "-" +
          formatIntizaDate(moment().add(-1, "day").toDate());

        totalFilters.push({
          operator: 0,
          field: "-1024",
          value: value
        });
      }
    }

    if (filterType === FilterCollection.Client && dashboard.GroupBy === -7) {
      if (dashboard.Variable === 3) {
        const value =
          formatIntizaDate(
            new Date(new Date().setFullYear(new Date().getFullYear() - 50))
          ) +
          "-" +
          formatIntizaDate(moment().add(-1, "day").toDate());
        totalFilters.push({
          operator: 0,
          field: "-1024",
          value: value
        });
      }
    }

    if (dashboard.Variable === 7) {
      totalFilters.push({
        operator: 0,
        field: "-1016",
        value: "1"
      });
    }

    if (dashboard.GroupBy === -33) {
      const val = CompanyService.GetAgeingValues()[data[3]];
      totalFilters.push({
        field: "-8",
        operator: data[8],
        value: val.filterVal
      });
    }

    if (addExtraFilters) {
      if (dashboard.Type === 9) {
        if (!totalFilters.find((el) => el.field === "-1012")) {
          totalFilters.push({
            operator: 5,
            field: "-1012",
            value: "0"
          });
        }
      }
    }

    if (dashboard.Filter) {
      totalFilters.push(
        ...parseDashboardFilter(
          dashboard.Filter,
          CompanyService.getAdditionalDefinitions()
        )
      );
    }
    const qs = encodeURIComponent(
      DashboardService.GetQueryFilter(totalFilters, filterType) || ""
    );
    if (newTab) {
      window.open(`/${currentLanguage}${url}?filter=${qs}`, "_blank");
    } else {
      history.push(`/${currentLanguage}${url}?filter=${qs}`);
    }
  };

  useEffect(() => {
    const toggleShowValues = (checked?: boolean) =>
      setHighchartOptions((x) => ({
        ...Object.assign(x, {
          plotOptions: {
            pie: {
              dataLabels: {
                format: checked
                  ? "{point.name}: {point.percentage:.1f} %<br>{point.y:,.0f}"
                  : "{point.name}: {point.percentage:.1f} %"
              },
              series: {
                animation: { duration: 0 }
              }
            },
            series: {
              dataLabels: {
                enabled: checked && dashboard.Type === 2
              }
            }
          },
          yAxis: {
            stackLabels: { enabled: checked }
          }
        })
      }));
    toggleShowValues(valuesEnabled);
  }, [valuesEnabled, dashboard.Type]);

  return (
    <div ref={chartContainerRef}>
      {/*<CheckBoxEditor onChange={showValues} message={TranslationService.translate.AlwaysShowValues}></CheckBoxEditor>*/}
      <HighchartsReact
        ref={chartRef}
        highcharts={Highcharts}
        options={highchartOptions}
      />
    </div>
  );
};

export default HighchartChart;
